/**
 * Loyalty Bottom Strip — theme-side full override.
 *
 * The Phase 2 spec requires 8 user cases (10 text variations) driven by the
 * customer's loyalty state + cart total. The bottom strip DOM is rendered by
 * the test-app-loccitane app block whose JS routes off a boolean setting and
 * ships stale/incomplete text and missing UC signals (welcomeVoucher). Since
 * we already expose /api/loyalty/summary with every field the spec needs, use
 * it as the source of truth from the theme, decide the UC per spec, and
 * rewrite the strip message + logo after each render.
 *
 * Guest-only spec branches read /cart.js; member branches read the summary
 * API. All copy lives here as constants so the strings match the handoff doc
 * exactly and are not overridden by settings_data.json (which the app-block
 * JS may still merge in).
 *
 * Dismissal: closing the strip hides it for the rest of the browsing session
 * (see isDismissed/markDismissed below), not just for one page navigation.
 */
(function () {
  var GIFT_THRESHOLD_ILS = 250;
  // Direct backend call — the endpoint returns Access-Control-Allow-Origin: *,
  // so storefront JS can hit it same as the checkout page does.
  var API_HOST = ((window.Shopify && Shopify.shop) || '').indexOf('staging') !== -1
    ? 'https://loccitane-shopify-stg.matat.io'
    : 'https://loccitane-shopify.matat.io';
  var SUMMARY_URL = API_HOST + '/api/loyalty/summary';
  var LOYALTY_URL = '/pages/club-benefits';
  var LOGIN_URL = '/account/login';
  var REGISTER_URL = '/account/register';

  // Spec copy — the merchant edits these in the Matat app admin
  // (Settings → Loyalty Strip Texts). The strings below are built-in
  // defaults; they're overwritten by fetchStripCopy() on boot when the
  // API responds. Keys must match app/Models/LoyaltyStripText::TEXT_KEYS.
  //   guest_empty  ← storefront-side key; the API returns 'guest_generic'.
  //   earn/join/... ← 1:1 with the API keys.
  var TPL = {
    guest_empty: 'הגיע הזמן להצטרף למועדון ״המסע לפרובאנס״! צבירת נקודות, מבצעים בלעדיים, מתנה בכל חודש ועוד {cta}',
    guest_cart:  'בהצטרפות למועדון ״המסע לפרובאנס״ רכישה זו תעניק לך <b>{pts} נקודות בשווי {value} ש"ח</b> {cta}',
    guest_gift:  'בהצטרפות למועדון ״המסע לפרובאנס״ רכישה זו תעניק לך <b>מתנה מפנקת וצבירת {pts} נקודות!</b> {cta}',
    welcome:     'בונז׳ור {name}, איזה כיף! יש לך <b>{amount} ש"ח הנחה</b> לקנייה הקרובה. בתוקף עד {date}',
    earn:        'בונז׳ור {name}, הטבות מועדון ״המסע לפרובאנס״ מחכות לך! רכישה זו תעניק לך <b>{pts} נקודות</b> נוספות',
    generic:     'בונז׳ור {name}, הטבות מועדון ״המסע לפרובאנס״ מחכות לך! {cta}',
    redeem:      'בונז׳ור {name}, הטבות מועדון ״המסע לפרובאנס״ מחכות לך! יש לך <b>{points} נקודות</b> לממש',
    birthday:    '<b>Félicitations</b> {name}, איזה כיף! יש לך <b>{percent}% הנחה*</b> לכבוד יום ההולדת שלך!',
    expired:     'בונז׳ור {name}, החברות במועדון הסתיימה ב-{date}, <b>{points} הנקודות</b> שלך שמורות לך! {cta}',
    expiring:    'בונז׳ור {name}, תוקף המועדון יסתיים בעוד {days} יום, יש לך <b>{points} נקודות</b> לממש! {cta}',
    join:        'בונז׳ור {name}, הצטרפו למועדון ״המסע לפרובאנס״ ותיהנו מצבירת נקודות, מבצעים ומתנה בכל חודש! {cta}',
    renewing:    'בונז׳ור {name}, הטבות מועדון ״המסע לפרובאנס״ מחכות לך! יש לך <b>{points} נקודות</b> למימוש',
    // Non-member (logged in) with the club-membership product already in cart —
    // dedicated copy so the strip stops falling through to the generic earn
    // branch while the join is in progress.
    join_in_cart: 'בונז׳ור {name}, הטבות מועדון ״המסע לפרובאנס״ מחכות לך! רכישה זו תעניק לך <b>{pts} נקודות</b> נוספות'
  };
  var LABELS = {
    join: 'הצטרפות',
    login: 'התחברות',
    renew_expired: 'חידוש המועדון ל-24 חודשים',
    renew_expiring: 'לחידוש החברות',
    benefits: 'לכל ההטבות שלך'
  };

  // Prefer a signal Liquid actually populates in this theme — the customer
  // id on window.__loyaltyStripCustomer is present iff Shopify has a
  // session. The two older signals stay as fallbacks in case the layout
  // stops emitting the id.
  var LOGGED_IN = !!(window.__loyaltyStripCustomer && window.__loyaltyStripCustomer.id)
              || !!(window.__loyaltyStrip && window.__loyaltyStrip.loggedIn)
              || !!document.body.classList.contains('customer-logged-in');

  var applying = false;
  var lastSummary = null;
  var lastCart = null;
  var refreshTimer = null;

  function esc(s) { return String(s == null ? '' : s); }
  function sub(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, function (m, k) {
      if (!vars || !(k in vars)) return '';
      return k === 'cta' ? vars[k] : esc(vars[k]);
    });
  }

  function actionLink(action, href, text) {
    return '<a class="loyalty-strip__link" href="' + esc(href) + '" data-loyalty-action="' + action + '">' + esc(text) + '</a>';
  }
  function plainLink(href, text) {
    return '<a class="loyalty-strip__link" href="' + esc(href) + '">' + esc(text) + '</a>';
  }
  function guestCta()   { return actionLink('register', REGISTER_URL, LABELS.join) + ' \\ ' + actionLink('login', LOGIN_URL, LABELS.login); }
  function benefitsCta(){ return plainLink(LOYALTY_URL, LABELS.benefits); }
  // Renewal is not a navigation — clicking adds the club membership product
  // to cart so the customer can check out and renew in one step.
  function renewClub()  { return actionLink('renew-add', LOYALTY_URL, LABELS.renew_expired); }
  function renewMember(){ return actionLink('renew-add', LOYALTY_URL, LABELS.renew_expiring); }
  function joinCta()    { return actionLink('clubpopup', REGISTER_URL, LABELS.join); }

  // Format a date value coming back from the summary API into DD.MM.YYYY per spec.
  function fmtDate(raw) {
    if (!raw) return '';
    var d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    var pad = function (n) { return n < 10 ? '0' + n : String(n); };
    return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
  }

  function decide(summary, cart) {
    var recentRenewalAt = 0;
    try { recentRenewalAt = (typeof window.__clubRenewedAt === 'function') ? window.__clubRenewedAt() : 0; } catch (e) {}

    if (summary && (recentRenewalAt || (cart && cart.hasClubProduct))) {
      if (recentRenewalAt && summary.isClubMember && !summary.isExpired && !summary.isExpiringSoon) {
        try { if (typeof window.__clubRenewedClear === 'function') window.__clubRenewedClear(); } catch (e) {}
      } else if (recentRenewalAt && (summary.isExpired || summary.isExpiringSoon) && !(cart && cart.hasClubProduct)) {
        try { if (typeof window.__clubRenewedClear === 'function') window.__clubRenewedClear(); } catch (e) {}
      } else if (recentRenewalAt && !summary.isClubMember && !summary.isTemporaryMember && !(cart && cart.hasClubProduct)) {
        try {
          if (typeof window.__clubRenewedClear === 'function') window.__clubRenewedClear();
          document.dispatchEvent(new CustomEvent('cart:updated'));
        } catch (e) {}
      } else if (cart && cart.hasClubProduct && summary.isClubMember && summary.isExpired) {
        summary = Object.assign({}, summary, { isRenewing: true });
      } else if (cart && cart.hasClubProduct && !summary.isClubMember) {
        summary = Object.assign({}, summary, {
          isJoining: true,
          isClubMember: true,
          isExpired: false,
          isExpiringSoon: false
        });
      } else {
        summary = Object.assign({}, summary, {
          isClubMember: true,
          isExpired: false,
          isExpiringSoon: false
        });
      }
    }

    var cfgCustomerLive = window.__loyaltyStripCustomer || cfgCustomer;
    var cfgFirstName = cfgCustomerLive.firstName || '';
    var isGuest = !LOGGED_IN && (!summary || (!summary.clubCustomerId && !summary.firstName));
    if (isGuest) {
      var pts = pointsFor(cart && cart.earnTotal, summary);
      if (cart.total >= GIFT_THRESHOLD_ILS) {
        return { key: 'guest_gift', html: sub(TPL.guest_gift, { pts: pts, points: pts, cta: guestCta() }), logoRole: 'default' };
      }
      if (cart.total > 0) {
        return { key: 'guest_cart', html: sub(TPL.guest_cart, { pts: pts, points: pts, value: pts, cta: guestCta() }), logoRole: 'default' };
      }
      return { key: 'guest_empty', html: sub(TPL.guest_empty, { cta: guestCta() }), logoRole: 'default' };
    }
    var name = (summary && summary.firstName) || cfgFirstName;
    if (!summary || !summary.isClubMember) {
      var joinTpl = TPL.join;
      if (!name) {
        joinTpl = joinTpl
          .replace(/\s*<b[^>]*>\s*\{name\}\s*<\/b>\s*,\s*/i, '')
          .replace(/\s*\{name\}\s*,\s*/, '');
      }
      return { key: 'join', html: sub(joinTpl, { name: name, cta: joinCta() }), logoRole: 'default' };
    }
    if (summary.isRenewing) {
      return { key: 'renewing', html: sub(TPL.renewing, {
        name: name, points: summary.balancePoints || 0
      }), logoRole: 'default' };
    }
    if (summary.isJoining) {
      var joinPts = pointsFor(cart && cart.earnTotal, summary);
      return { key: 'join_in_cart', html: sub(TPL.join_in_cart, {
        name: name, pts: joinPts, points: joinPts, earn: joinPts
      }), logoRole: 'default' };
    }
    if (summary.isExpired) {
      return { key: 'expired', html: sub(TPL.expired, {
        name: name, date: fmtDate(summary.expirationDate),
        points: summary.balancePoints || 0, cta: renewClub()
      }), logoRole: 'default' };
    }
    if (summary.isExpiringSoon) {
      return { key: 'expiring', html: sub(TPL.expiring, {
        name: name, days: (summary.daysUntilExpiry != null ? summary.daysUntilExpiry : 'X'),
        points: summary.balancePoints || 0, cta: renewMember()
      }), logoRole: 'default' };
    }
    if (summary.birthdayEligible) {
      return { key: 'birthday', html: sub(TPL.birthday, {
        name: name, percent: summary.birthdayPercentage || 20
      }), logoRole: 'birthday' };
    }
    if (summary.welcomeVoucherEligible) {
      var expiry = '';
      if (summary.welcomeVoucherDaysLeft != null) {
        var d = new Date();
        d.setDate(d.getDate() + Number(summary.welcomeVoucherDaysLeft));
        expiry = fmtDate(d);
      }
      return { key: 'welcome', html: sub(TPL.welcome, {
        name: name, amount: summary.welcomeVoucherAmount || 40, date: expiry
      }), logoRole: 'default' };
    }
    var balance = summary.balancePoints || 0;
    var minRedeem = summary.minRedeem || 50;
    if (balance >= minRedeem) {
      return { key: 'redeem', html: sub(TPL.redeem, { name: name, points: balance }), logoRole: 'default' };
    }
    var earnPts = pointsFor(cart && cart.earnTotal, summary);
    if (earnPts > 0) {
      return { key: 'earn', html: sub(TPL.earn, { name: name, earn: earnPts, pts: earnPts, points: earnPts }), logoRole: 'default' };
    }
    return { key: 'generic', html: sub(TPL.generic, { name: name, cta: benefitsCta() }), logoRole: 'default' };
  }

  var STRIP_COPY_URL = API_HOST + '/api/loyalty/strip-copy';
  function fetchStripCopy() {
    var shop = (window.Shopify && Shopify.shop) || '';
    var url = STRIP_COPY_URL + (shop ? ('?shop=' + encodeURIComponent(shop)) : '');
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j || !j.success) return;
        var t = j.texts || {};
        var pairs = {
          guest_empty: t.guest_generic,
          guest_cart: t.guest_cart,
          guest_gift: t.guest_gift,
          welcome: t.welcome,
          earn: t.earn,
          generic: t.generic,
          redeem: t.redeem,
          birthday: t.birthday,
          expired: t.expired,
          expiring: t.expiring,
          join: t.member_join,
          renewing: t.renew_in_cart,
          join_in_cart: t.join_in_cart
        };
        Object.keys(pairs).forEach(function (k) {
          if (typeof pairs[k] === 'string' && pairs[k].length) TPL[k] = pairs[k];
        });
        var c = (j.ctaLabels && typeof j.ctaLabels === 'object' && !Array.isArray(j.ctaLabels)) ? j.ctaLabels : {};
        ['join', 'login', 'renew_expired', 'renew_expiring', 'benefits'].forEach(function (k) {
          if (typeof c[k] === 'string' && c[k].length) LABELS[k] = c[k];
        });
      })
      .catch(function () {});
  }

  function fetchSummary() {
    var cfg = window.__loyaltyStripCustomer || {};
    var customerId = cfg.id || '';
    var body = { customer_id: customerId ? String(customerId) : '', shop: (window.Shopify && Shopify.shop) || '' };
    return fetch(SUMMARY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().catch(function () { return null; }); })
      .catch(function () { return null; });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c) return { total: 0, originalTotal: 0, earnTotal: 0, items: 0, hasClubProduct: false };
        var total = (c.total_price || 0) / 100;
        var hasClub = false;
        var clubLineTotalCents = 0;
        var clubVariantId = window.__clubVariantId ? String(window.__clubVariantId) : '';
        var clubProductId = window.__clubProductId ? String(window.__clubProductId) : '';
        if (Array.isArray(c.items)) {
          c.items.forEach(function (i) {
            var isClub = false;
            if (clubVariantId && String(i.variant_id) === clubVariantId) isClub = true;
            if (clubProductId && String(i.product_id) === clubProductId) isClub = true;
            if (i.product_id === 8307650560192) isClub = true;
            if (isClub) {
              hasClub = true;
              clubLineTotalCents += (i.final_line_price != null ? i.final_line_price : i.line_price) || 0;
            }
          });
        }
        var earnTotalCents = Math.max(0, (c.total_price || 0) - clubLineTotalCents);
        var earnTotal = earnTotalCents / 100;
        var originalTotalCents = c.original_total_price != null ? c.original_total_price : (c.items_subtotal_price != null ? c.items_subtotal_price : c.total_price || 0);
        var originalTotal = Math.max(0, originalTotalCents - clubLineTotalCents) / 100;
        return {
          total: total,
          originalTotal: originalTotal,
          earnTotal: earnTotal,
          items: c.item_count || 0,
          hasClubProduct: hasClub
        };
      })
      .catch(function () { return { total: 0, originalTotal: 0, earnTotal: 0, items: 0, hasClubProduct: false }; });
  }

  function pointsFor(earnTotal, summary) {
    var ratio = (summary && typeof summary.pointsRatio === 'number') ? summary.pointsRatio
              : (typeof window.__pointsRatio === 'number' ? window.__pointsRatio : 0.1);
    return Math.round((earnTotal || 0) * ratio);
  }

  function refreshDrawerLoyaltyCopy(summary) {
    if (!summary || typeof summary.pointsRatio !== 'number') return;
    var containers = document.querySelectorAll('.cart-drawer-loyalty[data-earn-cents]');
    if (!containers.length) return;
    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];
      var earnCents = Number(container.getAttribute('data-earn-cents')) || 0;
      var customerName = container.getAttribute('data-customer-name') || '';
      var pts = Math.round((earnCents / 100) * summary.pointsRatio);
      var copies = container.querySelectorAll('.js-loyalty-earn-copy[data-copy-template]');
      for (var j = 0; j < copies.length; j++) {
        var el = copies[j];
        var tpl = el.getAttribute('data-copy-template') || '';
        el.innerHTML = tpl
          .replace(/\[\[points\]\]/g, String(pts))
          .replace(/\[\[customer_name\]\]/g, customerName);
      }
    }
  }

  function refreshPdpLoyaltyCopy(summary) {
    if (!summary || typeof summary.pointsRatio !== 'number') return;
    var els = document.querySelectorAll('.js-loyalty-pdp-earn-copy[data-copy-template]');
    if (!els.length) return;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var priceCents = Number(el.getAttribute('data-product-price-cents')) || 0;
      var pts = Math.ceil((priceCents / 100) * summary.pointsRatio);
      var tpl = el.getAttribute('data-copy-template') || '';
      el.innerHTML = tpl
        .replace(/\[\[points\]\]/g, String(pts))
        .replace(/\[\[club_points\]\]/g, String(pts));
    }
  }

  ['cart:updated', 'cart:refresh', 'cart:change', 'cart-drawer:open'].forEach(function (ev) {
    document.addEventListener(ev, function () {
      setTimeout(function () {
        if (window.__loyaltySummary) {
          refreshDrawerLoyaltyCopy(window.__loyaltySummary);
          refreshPdpLoyaltyCopy(window.__loyaltySummary);
        }
      }, 0);
    });
  });

  function findStrip() { return document.getElementById('loyalty-bottom-strip'); }

  // ---- Session-scoped dismissal --------------------------------------------
  // Closing the strip hides it on every page until the visitor starts a new
  // browsing session.
  //
  // History: the app block's own close handler wrote a localStorage flag that
  // was effectively permanent — the strip never came back, not even after
  // logout/login — so this override suppressed that handler and hid the strip
  // for one navigation only. That over-corrected: it reappeared on every page,
  // which is what customers reported.
  //
  // A session cookie is used rather than sessionStorage because sessionStorage
  // is per-tab, and opening a second tab is not a new session from the
  // visitor's point of view. sessionStorage is still written as a fallback for
  // contexts where cookies are unavailable. Both expire with the browsing
  // session, so the strip returns on a genuinely new one.
  var DISMISS_KEY = 'loyaltyStripDismissed';

  function isDismissed() {
    try { if (document.cookie.indexOf(DISMISS_KEY + '=1') !== -1) return true; } catch (e) {}
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; }
  }

  function markDismissed() {
    try { document.cookie = DISMISS_KEY + '=1; path=/; SameSite=Lax'; } catch (e) {}
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) {}
  }

  function hideStrip() {
    var root = findStrip();
    if (root) { root.hidden = true; root.style.display = 'none'; }
    document.body.classList.remove('has-loyalty-strip');
  }

  function renderInto(root, model) {
    var msg = root.querySelector('.loyalty-strip__msg');
    var logo = root.querySelector('.loyalty-strip__logo');
    if (!msg) return;
    root.style.visibility = '';
    root.__loyaltyOverridePainted = true;
    if (msg.innerHTML === model.html) return;
    applying = true;
    try {
      msg.innerHTML = model.html;
      if (logo && model.logoRole === 'birthday') {
        var birthdaySrc = (window.__loyaltyStrip && window.__loyaltyStrip.settings && window.__loyaltyStrip.settings.birthdayLogoUrl) || '';
        if (birthdaySrc) logo.style.backgroundImage = "url('" + birthdaySrc + "')";
      }
      bindCtaActions(msg);
    } finally {
      setTimeout(function () { applying = false; }, 0);
    }
  }

  function bindCtaActions(scope) {
    scope.querySelectorAll('[data-loyalty-action]').forEach(function (a) {
      if (a.__loyaltyBound) return;
      a.__loyaltyBound = true;
      a.addEventListener('click', function (e) {
        var act = a.getAttribute('data-loyalty-action');
        if (act === 'renew-add') {
          e.preventDefault();
          addClubProductToCart();
          return;
        }
        if (act === 'clubpopup') {
          var popup = document.querySelector('.club-member-popup-main');
          if (!popup) return;
          e.preventDefault();
          openPopupWhenHydrated(popup);
          return;
        }
        var trigger = act === 'login'
          ? document.querySelector('.header-account__link .header__icon--account')
          : (document.querySelector('.mlr_club_register') || document.querySelector('.link_signup_modal'));
        if (trigger) { e.preventDefault(); trigger.click(); }
      });
    });
  }

  function addClubProductToCart() {
    var VARIANT_ID = window.__clubVariantId;
    if (!VARIANT_ID) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var already = cart && Array.isArray(cart.items) && cart.items.some(function (i) {
          return String(i.variant_id) === String(VARIANT_ID);
        });
        if (already) {
          openDrawer();
          return;
        }
        var joinPromise = (typeof window.markClubJoiningOnBackend === 'function')
          ? window.markClubJoiningOnBackend(VARIANT_ID)
          : Promise.resolve();
        return joinPromise.then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: VARIANT_ID,
              quantity: 1,
              sections: 'cart-drawer,cart-icon-bubble',
              sections_url: window.location.pathname
            })
          })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (payload) {
              if (!payload) return;
              var drawer = document.querySelector('cart-drawer');
              if (drawer && typeof drawer.renderContents === 'function' && payload.sections) {
                try { drawer.renderContents(payload); } catch (e) {}
              }
              try {
                if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
                  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'loyalty-strip-renew', productVariantId: VARIANT_ID });
                }
              } catch (e) {}
            });
        });
      })
      .catch(function () {});
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      try { drawer.open(); } catch (e) {}
    }
  }

  function apply() {
    // Dismissed for this session — keep it hidden. This guard matters because
    // the MutationObserver below calls apply() on every cart/DOM change, which
    // would otherwise repaint the strip moments after the visitor closed it.
    if (isDismissed()) { hideStrip(); return; }
    var root = findStrip();
    if (!root) return;
    if (!lastSummary && !lastCart) return;
    var msg = root.querySelector('.loyalty-strip__msg');
    if (msg && msg.innerHTML.indexOf('<font') !== -1) return;
    var summary = lastSummary || null;
    var cart = lastCart || { total: 0, earnTotal: 0, items: 0 };
    var model = decide(summary, cart);
    if (!model) return;
    renderInto(root, model);
  }

  var copyLoaded = false;
  var refreshInFlight = false;
  var refreshPending = false;
  var cfgCustomer = window.__loyaltyStripCustomer || {};

  function refresh() {
    if (refreshInFlight) { refreshPending = true; return; }
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshInFlight = true;
      var copyPromise = copyLoaded ? Promise.resolve() : fetchStripCopy().then(function () { copyLoaded = true; });
      Promise.all([copyPromise, fetchSummary(), fetchCart()]).then(function (res) {
        lastSummary = res[1];
        lastCart = res[2];
        apply();
        try {
          window.__loyaltySummary = lastSummary;
          window.__loyaltyCart = lastCart;
          document.dispatchEvent(new CustomEvent('loyalty:updated', {
            detail: { summary: lastSummary, cart: lastCart }
          }));
          refreshDrawerLoyaltyCopy(lastSummary);
          refreshPdpLoyaltyCopy(lastSummary);
        } catch (e) {}
      }).catch(function () {}).then(function () {
        refreshInFlight = false;
        if (refreshPending) { refreshPending = false; refresh(); }
      });
    }, 60);
  }

  var LOADER_ID = 'loyalty-hydrating-loader';
  var HYDRATE_LOTTIE_URL = 'https://cdn.shopify.com/s/files/1/0703/7219/6544/files/OCC_Loader_anim_LO_White_1_95680804-1404-47c7-8d9d-cd79a4d07b54.json?v=1773146138';
  var HYDRATE_TIMEOUT_MS = 8000;

  function ensureStandaloneLoader() {
    var existing = document.getElementById(LOADER_ID);
    if (existing) return existing;
    var el = document.createElement('div');
    el.id = LOADER_ID;
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-label', 'טוען פרטים');
    el.style.cssText =
      'position:fixed;top:0;left:0;width:100%;height:100%;' +
      'background:rgba(255,255,255,.85);display:none;' +
      'align-items:center;justify-content:center;z-index:99999;';
    var container = document.createElement('div');
    container.style.cssText = 'width:100px;height:100px;';
    el.appendChild(container);
    document.body.appendChild(el);
    return el;
  }

  function showStandaloneLoader() {
    var el = ensureStandaloneLoader();
    if (el.style.display === 'flex') return;
    el.style.display = 'flex';
    if (typeof window.lottie !== 'undefined' && typeof window.lottie.loadAnimation === 'function') {
      try {
        el.__lottieAnim = window.lottie.loadAnimation({
          container: el.firstElementChild,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          path: HYDRATE_LOTTIE_URL
        });
      } catch (e) {}
    }
  }

  function hideStandaloneLoader() {
    var el = document.getElementById(LOADER_ID);
    if (!el || el.style.display === 'none') return;
    if (el.__lottieAnim && typeof el.__lottieAnim.destroy === 'function') {
      try { el.__lottieAnim.destroy(); } catch (e) {}
      el.__lottieAnim = null;
    }
    el.style.display = 'none';
  }

  function openPopupWhenHydrated(popup) {
    if (!window.__loyaltyNeedsHydration) {
      popup.style.display = 'flex';
      return;
    }
    showStandaloneLoader();
    var opened = false;
    function reveal() {
      if (opened) return;
      opened = true;
      document.removeEventListener('customer:hydrated', reveal);
      hideStandaloneLoader();
      popup.style.display = 'flex';
    }
    document.addEventListener('customer:hydrated', reveal, { once: true });
    setTimeout(reveal, HYDRATE_TIMEOUT_MS);
  }

  function updateBannerCopy(firstName) {
    if (!firstName) return;
    var titleEl = document.querySelector('[data-loyalty-banner-title]');
    if (titleEl) {
      var titleTpl = titleEl.getAttribute('data-title-generic-tpl');
      if (titleTpl && titleTpl.indexOf('[name]') !== -1) {
        var newTitle = titleTpl.split('[name]').join(firstName);
        titleEl.setAttribute('data-title-generic', newTitle);
        if (titleEl.getAttribute('data-is-birth-month') !== 'true') {
          titleEl.textContent = newTitle;
        }
      }
    }
    var subEl = document.querySelector('[data-loyalty-banner-subtitle]');
    if (subEl) {
      var subTpl = subEl.getAttribute('data-subtitle-generic-tpl');
      if (subTpl && subTpl.indexOf('[name]') !== -1) {
        var newSub = subTpl.split('[name]').join(firstName);
        subEl.setAttribute('data-subtitle-generic', newSub);
        if (titleEl && titleEl.getAttribute('data-is-birth-month') !== 'true') {
          subEl.textContent = newSub;
        }
      }
    }
  }

  function updatePopupInputs(data) {
    var pairs = [
      ['#CustomerFirstName', data.first_name],
      ['#CustomerLastName', data.last_name],
      ['#CustomerEmail', data.email],
      ['#CustomerPhone', data.phone]
    ];
    pairs.forEach(function (p) {
      if (!p[1]) return;
      var el = document.querySelector(p[0]);
      if (!el || el.value) return;
      el.value = p[1];
      try { el.dispatchEvent(new Event('input', { bubbles: true })); } catch (e) {}
    });
  }

  function extractHydratePayload(sectionHtml) {
    if (!sectionHtml) return null;
    var m = sectionHtml.match(/<script[^>]*data-customer-hydrate[^>]*>([\s\S]*?)<\/script>/);
    if (!m) return null;
    try { return JSON.parse(m[1]); } catch (e) { return null; }
  }

  function fetchHydrate() {
    return fetch('/?sections=customer-hydrate', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j) return null;
        return extractHydratePayload(j['customer-hydrate']);
      })
      .catch(function () { return null; });
  }

  function hydrateCustomer() {
    if (!window.__loyaltyNeedsHydration) return;
    var delays = [1500, 3500, 6000];
    var attempt = 0;

    function tick() {
      fetchHydrate().then(function (data) {
        if (data && data.phone) {
          window.__loyaltyStripCustomer = window.__loyaltyStripCustomer || {};
          if (data.first_name) window.__loyaltyStripCustomer.firstName = data.first_name;
          window.__loyaltyNeedsHydration = false;
          updatePopupInputs(data);
          if (data.first_name) updateBannerCopy(data.first_name);
          try {
            document.dispatchEvent(new CustomEvent('customer:hydrated', { detail: data }));
          } catch (e) {}
          apply();
          return;
        }
        if (data) updatePopupInputs(data);
        if (attempt < delays.length) {
          setTimeout(tick, delays[attempt++]);
        }
      });
    }
    setTimeout(tick, delays[attempt++]);
  }
  hydrateCustomer();

  function watchStrip() {
    var root = findStrip();
    if (!root) return false;
    if (!root.__loyaltyOverridePainted) root.style.visibility = 'hidden';
    var pending = false;
    var obs = new MutationObserver(function () {
      if (applying || pending) return;
      var msg = root.querySelector('.loyalty-strip__msg');
      if (msg && msg.innerHTML.indexOf('<font') !== -1) return;
      pending = true;
      setTimeout(function () { pending = false; apply(); }, 30);
    });
    obs.observe(root, { childList: true, subtree: true });
    return true;
  }

  // Close button. The strip DOM (including this button) is rendered by the
  // Matat app block, whose own handler writes its own dismissal flag — we
  // intercept in the capture phase so only one mechanism is in play, record the
  // dismissal for this session, and hide the strip.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var closer = t.closest('.loyalty-strip__close, [data-loyalty-close], .loyalty-bottom-strip__close');
    if (!closer) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    markDismissed();
    hideStrip();
  }, true);

  function boot() {
    // Respect a dismissal made earlier in this session before anything paints.
    if (isDismissed()) hideStrip();
    refresh();
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (watchStrip() || tries > 40) {
        clearInterval(timer);
        refresh();
      }
    }, 250);

    ['cart:updated', 'cart:refresh', 'cart:change', 'cart-drawer:open'].forEach(function (ev) {
      document.addEventListener(ev, refresh);
    });
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
      }
    } catch (e) {}

    var orig = window.fetch;
    if (orig && !orig.__loyaltyStripOverridePatched) {
      window.fetch = function () {
        var p = orig.apply(this, arguments);
        try {
          var a0 = arguments[0];
          var u = (a0 && a0.url) ? a0.url : (typeof a0 === 'string' ? a0 : '');
          if (u && /\/cart\/(add|change|update|clear)(\.js)?/.test(u)) {
            p.then(refresh).catch(function () {});
          }
        } catch (e) {}
        return p;
      };
      window.fetch.__loyaltyStripOverridePatched = true;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();